import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { Component, Input, OnInit, OnDestroy, HostBinding, Optional, Self, ElementRef } from '@angular/core';
import { FormGroup, FormControl, NgControl } from '@angular/forms';
import { MatFormFieldControl } from '@angular/material/form-field';
import { Subject } from 'rxjs';

@Component({
  selector: 'cm-date-time-control',
  templateUrl: './date-time-control.component.html',
  styleUrls: ['./date-time-control.component.scss'],
  providers: [{ provide: MatFormFieldControl, useExisting: DateTimeControlComponent }],
})
export class DateTimeControlComponent implements MatFormFieldControl<Date>, OnDestroy {
  static nextId = 0;
  @HostBinding() id = `date-time-input-${DateTimeControlComponent.nextId++}`;

  stateChanges = new Subject<void>();

  onChangeFns = new Array<(value: any) => void>();
  onTouchFns = new Array<() => void>();

  @Input()
  get value(): Date | null {
    if (!this.dateControl.value || !this.timeControl.value) {
      return null;
    }

    const date = new Date(`${this.dateControl.value}`);
    const time = this.timeControl.value;

    const [hours, minutes] = time.split(':');

    date.setHours(hours);
    date.setMinutes(minutes);

    return date;
  }

  set value(val: Date | null) {
    const date = val ? new Date(val) : new Date();

    this.formGroup.get('date').setValue(date);
    this.formGroup.get('time').setValue(`${this.addTrailingZero(date.getHours())}:${this.addTrailingZero(date.getMinutes())}`);
  }

  @Input()
  get placeholder() {
    return this._placeholder;
  }
  set placeholder(plh) {
    this._placeholder = plh;
    this.stateChanges.next();
  }
  private _placeholder: string;

  focused = false;
  touched = false;

  onFocusIn(event: FocusEvent) {
    if (!this.focused) {
      this.focused = true;
      this.stateChanges.next();
    }
  }

  onFocusOut(event: FocusEvent) {
    if (!this.elementRef.nativeElement.contains(event.relatedTarget as Element)) {
      this.touched = true;
      this.focused = false;
      this.onTouchFns.forEach((fn) => fn());
      this.stateChanges.next();
    }
  }

  get empty() {
    return !this.dateControl.value && !this.timeControl.value;
  }

  @HostBinding('class.floating')
  get shouldLabelFloat() {
    return this.focused || !this.empty;
  }

  @Input()
  get required() {
    return this._required;
  }
  set required(req) {
    this._required = coerceBooleanProperty(req);
    this.stateChanges.next();
  }
  private _required = false;

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(value: boolean) {
    this._disabled = coerceBooleanProperty(value);
    this._disabled ? this.formGroup.disable() : this.formGroup.enable();
    this.stateChanges.next();
  }
  private _disabled = false;

  get errorState(): boolean {
    return this.formGroup.invalid && this.touched;
  }

  controlType = 'date-time-control';

  get dateControl() {
    return this.formGroup.get('date');
  }

  get timeControl() {
    return this.formGroup.get('time');
  }

  formAttributes = {
    date: 'date',
    time: 'time',
  };

  formGroup = new FormGroup({
    [this.formAttributes.date]: new FormControl(null),
    [this.formAttributes.time]: new FormControl(null),
  });

  constructor(@Optional() @Self() public ngControl: NgControl, private elementRef: ElementRef) {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    this.formGroup.valueChanges.subscribe(() => {
      this.propagateValue(this.value);
    });
  }

  ngOnDestroy() {
    this.stateChanges.complete();
  }

  setDescribedByIds(ids: string[]) {
    if (!this.elementRef) {
      return;
    }
    const controlElement = this.elementRef.nativeElement.querySelector('.date-time-control-container')!;
    controlElement.setAttribute('aria-describedby', ids.join(' '));
  }

  onContainerClick(event: MouseEvent) {
    if ((event.target as Element).tagName.toLowerCase() != 'input') {
      this.elementRef.nativeElement.querySelector('input').focus();
    }
  }

  writeValue(val: Date) {
    this.value = val;
  }

  /**
   * ControlValueAccessor change event propagation
   * @param fn
   */
  registerOnChange(fn: any): void {
    this.onChangeFns.push(fn);
  }

  /**
   * ControlValueAccessor touch event propagation
   * @param fn
   */
  registerOnTouched(fn: any) {
    this.onTouchFns.push(fn);
  }

  propagateValue(value: any) {
    this.onChangeFns.forEach((fn) => fn(value));
  }

  private addTrailingZero(value: number) {
    return value < 10 ? `0${value}` : `${value}`;
  }
}
